import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../General/Utils/Endpoints';
import { API_BASE } from '../../../../api';
import { normalizeValue } from '../../../../utils/Texts';
import { getSessionEmpresaId } from '../../../../utils/SessionUser';
import './Organigrama.css';

// ── Helper: Construir URL completa de imagen desde path relativo ──────────────
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  // El servidor base es API_BASE sin el segmento /api
  const server = API_BASE.split('/api')[0];
  return `${server}${path}`;
};


// ── Constantes de layout ──────────────────────────────────────────────────────
// Jerarquía: Empresa → ÁreaNegocio → Empleado → UnidadOrg
const LEVELS = {
  empresa: { x: 60, color: '#059669', icon: '🏛️', label: 'EMPRESA' },
  areas: { x: 340, color: '#0ea5e9', icon: '🏢', label: 'ÁREA NEG.' },
  empleados: { x: 620, color: '#e11d48', icon: '👤', label: 'EMPLEADO' },
  unidades: { x: 900, color: '#4f46e5', icon: '📂', label: 'UNIDAD ORG.' },
};
const NODE_H_GAP = 160; // px verticales entre nodos del mismo nivel
const NODE_START_Y = 60;

export default function Organigrama() {
  const [rawData, setRawData] = useState({
    unidades: [], empleados: [], areas: [], empresas: [],
    relEmpleadoArea: [], relEmpleadoUO: [],
    relEmpleadoPuesto: [], puestosData: [],
    fotosEmpleados: {}
  });
  const [loading, setLoading] = useState(true);

  // Nodos en canvas y conexiones
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);

  // Sidebar colapsable
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Modo conectar manual
  const [connectMode, setConnectMode] = useState(false);
  const [connectingFrom, setConnectingFrom] = useState(null);

  // Movimiento de nodos
  const [draggingNodeId, setDraggingNodeId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const canvasRef = useRef(null);

  // Auto-save state and refs
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const prevGraphRef = useRef('');
  const isFirstLoadRef = useRef(true);
  const isSavingRef = useRef(false);

  // ── Carga de datos ──────────────────────────────────────────────────────────
  const fetchAll = useCallback(async (forceReloadDB = false) => {
    setLoading(true);
    try {
      const [resUO, resEmp, resAreas, resEmpresas, resRelArea, resRelUO, resRelPuesto, resPuestos] = await Promise.all([
        axios.get(ENDPOINTS.unidadesOrg),
        axios.get(ENDPOINTS.empleados),
        axios.get(ENDPOINTS.areasNegocios),
        axios.get(ENDPOINTS.empresas),
        axios.get(ENDPOINTS.empleadosAreaNegocio),
        axios.get(ENDPOINTS.unidadesOrgEmpleado),
        axios.get(ENDPOINTS.empleadosPuesto).catch(() => ({ data: [] })),
        axios.get(ENDPOINTS.puestosSecurity('propias')).catch(() => ({ data: [] })),
      ]);

      const empleadosRaw = resEmp.data || [];

      // ── Cargar fotos de personas para todos los empleados (en paralelo) ──
      const fotosMap = {};
      if (empleadosRaw.length > 0) {
        const fotoPromises = empleadosRaw.map(async (emp) => {
          const idPersona = emp.idPersona ?? emp.IdPersona;
          const idEmpleado = emp.idEmpleado ?? emp.IdEmpleado;
          if (!idPersona || !idEmpleado) return;
          try {
            const resP = await axios.get(ENDPOINTS.personaById(idPersona));
            const p = resP.data;
            const foto = p.fotoPerfil ?? p.FotoPerfil ?? '';
            if (foto) fotosMap[idEmpleado] = getImageUrl(foto);
          } catch { /* sin foto */ }
        });
        await Promise.all(fotoPromises);
      }

      const data = {
        unidades: resUO.data || [],
        empleados: empleadosRaw,
        areas: resAreas.data || [],
        empresas: resEmpresas.data || [],
        relEmpleadoArea: resRelArea.data || [],
        relEmpleadoUO: resRelUO.data || [],
        relEmpleadoPuesto: resRelPuesto.data || [],
        puestosData: resPuestos.data || [],
        fotosEmpleados: fotosMap,
      };
      setRawData(data);

      const empId = getSessionEmpresaId();
      let savedOrg = null;
      if (!forceReloadDB && empId) {
        try {
          const resOrg = await axios.get(ENDPOINTS.organigramaByEmpresa(empId));
          savedOrg = resOrg.data;
        } catch (err) {
          console.log('No saved organigrama found for company', empId);
        }
      }

      if (savedOrg && savedOrg.Organigrama) {
        try {
          const parsed = JSON.parse(savedOrg.Organigrama);

          // Re-enriquecer nodos de empleado con fotos y puestos actuales
          // (los nodos guardados en BD pueden no tener _fotoUrl ni _puestoNombre)
          const puestoByIdMap = new Map((data.puestosData || []).map(p => [p.idPuesto || p.IdPuesto, p]));
          const puestoNombreByEmp = new Map();
          (data.relEmpleadoPuesto || []).forEach(rel => {
            const idEmp = rel.idEmpleado || rel.IdEmpleado;
            const idPuesto = rel.idPuesto || rel.IdPuesto;
            if (!puestoNombreByEmp.has(idEmp)) {
              const puesto = puestoByIdMap.get(idPuesto);
              if (puesto) puestoNombreByEmp.set(idEmp, puesto.nombre || puesto.Nombre || '');
            }
          });

          const enrichedNodes = (parsed.nodes || []).map(node => {
            if (node.type !== 'empleados') return node;
            return {
              ...node,
              itemData: {
                ...node.itemData,
                _fotoUrl: fotosMap[node.dataId] || null,
                _puestoNombre: puestoNombreByEmp.get(node.dataId) || node.itemData?._puestoNombre || '',
              }
            };
          });

          setNodes(enrichedNodes);
          setConnections(parsed.connections || []);
          prevGraphRef.current = JSON.stringify({ nodes: enrichedNodes, connections: parsed.connections || [] });
          setTimeout(() => { isFirstLoadRef.current = false; }, 500);
        } catch (e) {
          console.error("Error parsing saved organigrama:", e);
          buildGraph(data);
        }
      } else {
        buildGraph(data);
      }
    } catch (error) {
      console.error('Error fetching organigrama data:', error);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Construir grafo desde BD ─────────────────────────────────────────────────
  const buildGraph = (data) => {
    const { unidades, empleados, areas, empresas, relEmpleadoArea, relEmpleadoUO, relEmpleadoPuesto, puestosData, fotosEmpleados = {} } = data;

    // Índices para lookup rápido
    const empById = new Map(empleados.map(e => [e.idEmpleado || e.IdEmpleado, e]));
    const areaById = new Map(areas.map(a => [a.idAreaNegocio || a.IdAreaNegocio, a]));
    const uoById = new Map(unidades.map(u => [u.idUnidadOrganizacional || u.IdUnidadOrganizacional, u]));
    const empById2 = new Map(empresas.map(em => [em.idEmpresa || em.IdEmpresa, em]));

    // Índice de puestos por idPuesto y relación empleado→puesto
    const puestoById = new Map((puestosData || []).map(p => [p.idPuesto || p.IdPuesto, p]));
    const puestoNombreByEmpleado = new Map();
    (relEmpleadoPuesto || []).forEach(rel => {
      const idEmp = rel.idEmpleado || rel.IdEmpleado;
      const idPuesto = rel.idPuesto || rel.IdPuesto;
      if (!puestoNombreByEmpleado.has(idEmp)) {
        const puesto = puestoById.get(idPuesto);
        if (puesto) {
          puestoNombreByEmpleado.set(idEmp, puesto.nombre || puesto.Nombre || '');
        }
      }
    });

    // Sets de IDs que tienen al menos una relación
    const connectedEmpIds = new Set();
    const connectedAreaIds = new Set();
    const connectedUOIds = new Set();
    const connectedEmpresaIds = new Set();

    // Normalizar relaciones
    const normRelArea = relEmpleadoArea.map(r => ({
      idEmpleado: r.idEmpleado || r.IdEmpleado,
      idAreaNegocio: r.idAreaNegocio || r.IdAreaNegocio,
    })).filter(r => r.idEmpleado && r.idAreaNegocio);

    const normRelUO = relEmpleadoUO.map(r => ({
      idEmpleado: r.idEmpleado || r.IdEmpleado,
      idUnidadOrganizacional: r.idUnidadOrganizacional || r.IdUnidadOrganizacional,
    })).filter(r => r.idEmpleado && r.idUnidadOrganizacional);

    normRelArea.forEach(r => {
      connectedEmpIds.add(r.idEmpleado);
      connectedAreaIds.add(r.idAreaNegocio);
    });
    normRelUO.forEach(r => {
      connectedEmpIds.add(r.idEmpleado);
      connectedUOIds.add(r.idUnidadOrganizacional);
    });

    // Áreas conectadas → su empresa también va al canvas
    connectedAreaIds.forEach(aId => {
      const area = areaById.get(aId);
      const idEmp = area?.idEmpresa || area?.IdEmpresa;
      if (idEmp) connectedEmpresaIds.add(idEmp);
    });

    // ── Crear nodos para cada elemento conectado ──
    const newNodes = [];
    const nodeIdMap = {}; // domainKey → canvasNodeId

    const makeNodeId = (type, dataId) => `${type}-${dataId}`;

    const addNode = (type, dataId, itemData) => {
      const nid = makeNodeId(type, dataId);
      if (nodeIdMap[nid]) return nid; // ya existe
      nodeIdMap[nid] = nid;

      // Posición: calcular índice dentro del nivel
      const levelConfig = LEVELS[type];
      const sameLevel = newNodes.filter(n => n.type === type);
      const y = NODE_START_Y + sameLevel.length * NODE_H_GAP;

      // Enriquecer empleados con nombre del puesto y foto de perfil
      const enrichedItemData = (type === 'empleados')
        ? {
            ...itemData,
            _puestoNombre: puestoNombreByEmpleado.get(dataId) || '',
            _fotoUrl: fotosEmpleados[dataId] || null,
          }
        : itemData;

      newNodes.push({
        id: nid,
        dataId,
        type,
        itemData: enrichedItemData,
        position: { x: levelConfig.x, y },
        fromDB: true,
      });
      return nid;
    };

    // 1. Empresas conectadas
    connectedEmpresaIds.forEach(id => {
      const item = empById2.get(id);
      if (item) addNode('empresa', id, item);
    });

    // 2. Áreas conectadas
    connectedAreaIds.forEach(id => {
      const item = areaById.get(id);
      if (item) addNode('areas', id, item);
    });

    // 3. Empleados conectados
    connectedEmpIds.forEach(id => {
      const item = empById.get(id);
      if (item) addNode('empleados', id, item);
    });

    // 4. Unidades conectadas
    connectedUOIds.forEach(id => {
      const item = uoById.get(id);
      if (item) addNode('unidades', id, item);
    });

    // ── Crear conexiones ──
    const newConnections = [];

    // Empresa → Área (via idEmpresa del área)
    connectedAreaIds.forEach(aId => {
      const area = areaById.get(aId);
      const idEmp = area?.idEmpresa || area?.IdEmpresa;
      if (idEmp && connectedEmpresaIds.has(idEmp)) {
        const fromId = makeNodeId('empresa', idEmp);
        const toId = makeNodeId('areas', aId);
        if (nodeIdMap[fromId] && nodeIdMap[toId]) {
          newConnections.push({
            id: `conn-emp-area-${idEmp}-${aId}`,
            from: fromId, to: toId, fromDB: true,
            label: 'pertenece a'
          });
        }
      }
    });

    // Empleado → Área
    normRelArea.forEach(r => {
      const fromId = makeNodeId('empleados', r.idEmpleado);
      const toId = makeNodeId('areas', r.idAreaNegocio);
      if (nodeIdMap[fromId] && nodeIdMap[toId]) {
        newConnections.push({
          id: `conn-emp-area-${r.idEmpleado}-${r.idAreaNegocio}`,
          from: fromId, to: toId, fromDB: true,
          label: 'en área'
        });
      }
    });

    // Empleado → UnidadOrg
    normRelUO.forEach(r => {
      const fromId = makeNodeId('empleados', r.idEmpleado);
      const toId = makeNodeId('unidades', r.idUnidadOrganizacional);
      if (nodeIdMap[fromId] && nodeIdMap[toId]) {
        newConnections.push({
          id: `conn-emp-uo-${r.idEmpleado}-${r.idUnidadOrganizacional}`,
          from: fromId, to: toId, fromDB: true,
          label: 'asignado a'
        });
      }
    });

    setNodes(newNodes);
    setConnections(newConnections);
    setConnectMode(false);
    setConnectingFrom(null);
    prevGraphRef.current = JSON.stringify({ nodes: newNodes, connections: newConnections });
    setTimeout(() => { isFirstLoadRef.current = false; }, 500);
  };

  const handleSaveToDb = async (silent = false) => {
    const empId = getSessionEmpresaId();
    if (!empId) return;

    if (isSavingRef.current) return;
    isSavingRef.current = true;

    if (!silent) setLoading(true);
    else setIsAutoSaving(true);

    try {
      const payload = {
        IdEmpresa: empId,
        Organigrama: JSON.stringify({ nodes, connections }),
        FechaCreacion: new Date().toISOString(),
        Activo: true
      };

      await axios.post(ENDPOINTS.organigramas, payload);
      prevGraphRef.current = JSON.stringify({ nodes, connections });
      console.log('Organigrama guardado con éxito.');
    } catch (error) {
      console.error('Error al guardar organigrama:', error);
    } finally {
      isSavingRef.current = false;
      if (!silent) setLoading(false);
      else setIsAutoSaving(false);
    }
  };

  useEffect(() => {
    if (isFirstLoadRef.current) return;
    if (loading || isSavingRef.current) return;

    const currentGraphStr = JSON.stringify({ nodes, connections });
    const graphChanged = currentGraphStr !== prevGraphRef.current;

    if (!graphChanged) return;

    setIsAutoSaving(true);

    const timer = setTimeout(async () => {
      if (isSavingRef.current) return;
      await handleSaveToDb(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [nodes, connections, loading]);

  // ── Sidebar: solo huérfanos ─────────────────────────────────────────────────
  const sidebarItems = (() => {
    const canvasDataIds = new Set(nodes.map(n => `${n.type}-${n.dataId}`));

    const orphanAreas = rawData.areas.filter(a => !canvasDataIds.has(`areas-${a.idAreaNegocio || a.IdAreaNegocio}`));
    const orphanEmp = rawData.empleados.filter(e => !canvasDataIds.has(`empleados-${e.idEmpleado || e.IdEmpleado}`));
    const orphanUO = rawData.unidades.filter(u => !canvasDataIds.has(`unidades-${u.idUnidadOrganizacional || u.IdUnidadOrganizacional}`));
    const orphanEmpresas = rawData.empresas.filter(em => !canvasDataIds.has(`empresa-${em.idEmpresa || em.IdEmpresa}`));

    return { orphanAreas, orphanEmp, orphanUO, orphanEmpresas };
  })();

  // ── Drag desde sidebar ──────────────────────────────────────────────────────
  const handleDragStartSidebar = (e, item, type) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ ...item, nodeType: type }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDropCanvas = (e) => {
    e.preventDefault();
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const rawX = e.clientX - rect.left + canvasRef.current.scrollLeft;
    const rawY = e.clientY - rect.top + canvasRef.current.scrollTop;

    // Clamp dentro del canvas inner (3000x3000) dejando margen del tamaño estimado del nodo
    const CANVAS_SIZE = 3000;
    const NODE_EST_W = 200;
    const NODE_EST_H = 120;
    const x = Math.max(0, Math.min(rawX, CANVAS_SIZE - NODE_EST_W));
    const y = Math.max(0, Math.min(rawY, CANVAS_SIZE - NODE_EST_H));

    const dragData = e.dataTransfer.getData('application/json');
    if (!dragData) return;

    try {
      const item = JSON.parse(dragData);
      const type = item.nodeType;
      const dataId = item.idUnidadOrganizacional || item.idEmpleado || item.idAreaNegocio
        || item.IdUnidadOrganizacional || item.IdEmpleado || item.IdAreaNegocio
        || item.idEmpresa || item.IdEmpresa;
      const nid = `${type}-${dataId}`;

      // No duplicar (empresa, areas, unidades, empleados: solo uno por canvas)
      if (nodes.some(n => n.id === nid)) return;

      // Enriquecer empleados arrastrados desde sidebar con nombre del puesto y foto
      let enrichedItem = item;
      if (type === 'empleados') {
        const { relEmpleadoPuesto, puestosData, fotosEmpleados = {} } = rawData;
        const puestoById2 = new Map((puestosData || []).map(p => [p.idPuesto || p.IdPuesto, p]));
        const rel = (relEmpleadoPuesto || []).find(r => (r.idEmpleado || r.IdEmpleado) === dataId);
        const puestoNombre = rel ? (puestoById2.get(rel.idPuesto || rel.IdPuesto)?.nombre || '') : '';
        enrichedItem = {
          ...item,
          _puestoNombre: puestoNombre,
          _fotoUrl: fotosEmpleados[dataId] || null,
        };
      }

      setNodes(prev => [...prev, {
        id: nid, dataId, type, itemData: enrichedItem,
        position: { x, y }, fromDB: false,
      }]);
    } catch (err) {
      console.error('Error parsing drop:', err);
    }
  };


  const handleDragOverCanvas = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // ── Movimiento de nodos ─────────────────────────────────────────────────────
  const handleNodePointerDown = (e, nodeId) => {
    if (connectMode) return;
    e.stopPropagation();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    const canvasContainer = canvasRef.current.parentElement;
    const offsetX = e.clientX - node.position.x + canvasContainer.scrollLeft;
    const offsetY = e.clientY - node.position.y + canvasContainer.scrollTop;
    setDraggingNodeId(nodeId);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handlePointerMove = useCallback((e) => {
    if (!draggingNodeId) return;
    const canvasContainer = canvasRef.current.parentElement;
    const rawX = e.clientX - dragOffset.x + canvasContainer.scrollLeft;
    const rawY = e.clientY - dragOffset.y + canvasContainer.scrollTop;

    // Clamp: el nodo no puede salir del canvas inner (3000x3000)
    const CANVAS_SIZE = 3000;
    const nodeEl = document.getElementById(`org-node-${draggingNodeId}`);
    const nodeW = nodeEl ? nodeEl.offsetWidth : 0;
    const nodeH = nodeEl ? nodeEl.offsetHeight : 0;
    const x = Math.max(0, Math.min(rawX, CANVAS_SIZE - nodeW));
    const y = Math.max(0, Math.min(rawY, CANVAS_SIZE - nodeH));

    setNodes(prev => prev.map(n => n.id === draggingNodeId ? { ...n, position: { x, y } } : n));
  }, [draggingNodeId, dragOffset]);


  const handlePointerUp = () => { if (draggingNodeId) setDraggingNodeId(null); };

  const handleRemoveNode = (id) => {
    setNodes(prev => prev.filter(n => n.id !== id));
    setConnections(prev => prev.filter(c => c.from !== id && c.to !== id));
  };

  // ── Modo conectar manual ────────────────────────────────────────────────────
  const toggleConnectMode = () => {
    setConnectMode(prev => !prev);
    setConnectingFrom(null);
  };

  const handleNodeClickConnect = (e, nodeId) => {
    if (!connectMode) return;
    e.stopPropagation();

    if (!connectingFrom) {
      setConnectingFrom(nodeId);
    } else {
      if (connectingFrom === nodeId) { setConnectingFrom(null); return; }
      const exists = connections.some(
        c => (c.from === connectingFrom && c.to === nodeId)
          || (c.from === nodeId && c.to === connectingFrom)
      );
      if (!exists) {
        setConnections(prev => [...prev, {
          id: `manual-${connectingFrom}-${nodeId}-${Date.now()}`,
          from: connectingFrom, to: nodeId, fromDB: false,
        }]);
      }
      setConnectingFrom(null);
    }
  };

  const handleDeleteConnection = (connId) => {
    setConnections(prev => prev.filter(c => c.id !== connId));
  };

  // ── Botones de toolbar ──────────────────────────────────────────────────────
  const handleClearAll = () => {
    setNodes([]);
    setConnections([]);
    setConnectingFrom(null);
    setConnectMode(false);
  };

  const handleReloadDB = () => {
    setNodes([]);
    setConnections([]);
    setConnectingFrom(null);
    setConnectMode(false);
    fetchAll(true);
  };

  // ── Helpers visuales ────────────────────────────────────────────────────────
  const getDisplayName = (item, type) => {
    if (type === 'empresa') return normalizeValue(item.nombre || item.Nombre || item.razonSocial || item.RazonSocial);
    if (type === 'areas') return normalizeValue(item.nombre || item.Nombre);
    if (type === 'unidades') return normalizeValue(item.nombre || item.Nombre);
    if (type === 'empleados') {
      const nombre = item.nombre || item.Nombre || '';
      const apPat = item.apellidoPaterno || item.ApellidoPaterno || '';
      const apMat = item.apellidoMaterno || item.ApellidoMaterno || '';
      const full = `${nombre} ${apPat} ${apMat}`.trim();
      if (full) return normalizeValue(full);
      // Fallback a objeto persona anidado
      const per = item.persona || item.Persona || {};
      return normalizeValue(`${per.nombre || per.Nombre || ''} ${per.apellidoPaterno || per.ApellidoPaterno || ''}`.trim()) || 'Sin nombre';
    }
    return 'Elemento';
  };

  const getLevelConfig = (type) => LEVELS[type] || { color: '#64748b', icon: '📌', label: type?.toUpperCase() };

  const getNodeCenter = (node) => {
    const el = document.getElementById(`org-node-${node.id}`);
    return el
      ? { x: node.position.x + el.offsetWidth / 2, y: node.position.y + el.offsetHeight / 2 }
      : { x: node.position.x + 100, y: node.position.y + 40 };
  };

  const empId = getSessionEmpresaId();
  const hasNoCompany = !empId || Number(empId) === 0;

  if (hasNoCompany) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', minHeight: '400px', backgroundColor: '#f3f4f6',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px', width: '100%', backgroundColor: '#ffffff',
          borderRadius: '12px', padding: '2.5rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          textAlign: 'center', borderTop: '4px solid #ef4444'
        }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            backgroundColor: '#fee2e2', color: '#ef4444',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem auto', fontSize: '32px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h2 style={{
            fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937',
            marginBottom: '1rem'
          }}>
            Acceso Restringido
          </h2>
          <p style={{
            color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6'
          }}>
            Lo sentimos, no podemos cargar el módulo de organigrama porque tu usuario no cuenta con la configuración necesaria.
          </p>

          <div style={{
            textAlign: 'left', backgroundColor: '#f9fafb',
            padding: '1rem 1.5rem', borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <p style={{
              fontSize: '0.875rem', fontWeight: '600', color: '#374151',
              marginTop: 0, marginBottom: '0.5rem'
            }}>
              Detalles del error:
            </p>
            <ul style={{
              margin: 0, paddingLeft: '1.25rem',
              fontSize: '0.875rem', color: '#ef4444'
            }}>
              <li style={{ marginBottom: '0.25rem' }}>El usuario de sesión no tiene una Empresa asignada.</li>
            </ul>
          </div>

          <div style={{ marginTop: '2rem', display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              className="organigrama-toolbar-btn primary"
              onClick={() => window.location.reload()}
              style={{ padding: '0.5rem 1rem' }}
            >
              Intentar recargar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="organigrama-container"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <div className="organigrama-toolbar">
        <button
          className={`organigrama-toolbar-btn ${connectMode ? 'active' : ''}`}
          onClick={toggleConnectMode}
          title="Activa el modo para conectar nodos manualmente"
        >
          🔗 {connectMode ? 'Salir de Conectar' : 'Modo Conectar'}
        </button>

        {nodes.length === 0 ? (
          <button
            className="organigrama-toolbar-btn primary"
            onClick={handleReloadDB}
            title="Carga las relaciones desde la base de datos"
          >
            ♻️ Cargar BD
          </button>
        ) : (
          <button
            className="organigrama-toolbar-btn danger"
            onClick={handleClearAll}
            title="Borra todos los nodos y conexiones del canvas"
          >
            🗑️ Limpiar Todo
          </button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', marginRight: '4px' }}>
          {isAutoSaving ? (
            <span style={{ fontSize: '0.82rem', color: '#0ea5e9', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              Guardando...
            </span>
          ) : (
            <span style={{ fontSize: '0.82rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
              ✓ Guardado
            </span>
          )}
        </div>

        {connectMode && (
          <span className="organigrama-toolbar-hint">
            {connectingFrom
              ? '✅ Origen seleccionado — click en el nodo destino'
              : '👆 Click en el nodo origen'}
          </span>
        )}
      </div>

      {/* ── Body ── */}
      <div className={`organigrama-body${sidebarOpen ? '' : ' organigrama-body--sidebar-collapsed'}`}>
        {/* Sidebar — solo huérfanos */}
        <div className="organigrama-sidebar-wrapper">
        <div className="organigrama-sidebar">
          <h2 className="organigrama-sidebar-title">Elementos sin asignar</h2>
          <p className="organigrama-sidebar-hint">
            Solo aparecen aquí los elementos sin relaciones en BD. Arrástralos al canvas para organizarlos.
          </p>

          {loading ? (
            <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Cargando datos...</p>
          ) : (
            <>
              {/* Empresas huérfanas */}
              {sidebarItems.orphanEmpresas.length > 0 && (
                <div className="organigrama-category">
                  <h3 className="organigrama-category-title">🏛️ Empresas ({sidebarItems.orphanEmpresas.length})</h3>
                  {sidebarItems.orphanEmpresas.map(em => {
                    const id = em.idEmpresa || em.IdEmpresa;
                    return (
                      <div key={id} className="organigrama-item"
                        style={{ borderLeft: `3px solid ${LEVELS.empresa.color}` }}
                        draggable
                        onDragStart={(e) => handleDragStartSidebar(e, em, 'empresa')}
                      >
                        <span className="organigrama-item-icon">🏛️</span>
                        <span>{getDisplayName(em, 'empresa')}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Áreas huérfanas */}
              {sidebarItems.orphanAreas.length > 0 && (
                <div className="organigrama-category">
                  <h3 className="organigrama-category-title">🏢 Áreas sin asignar ({sidebarItems.orphanAreas.length})</h3>
                  {sidebarItems.orphanAreas.map(a => {
                    const id = a.idAreaNegocio || a.IdAreaNegocio;
                    return (
                      <div key={id} className="organigrama-item"
                        style={{ borderLeft: `3px solid ${LEVELS.areas.color}` }}
                        draggable
                        onDragStart={(e) => handleDragStartSidebar(e, a, 'areas')}
                      >
                        <span className="organigrama-item-icon">🏢</span>
                        <span>{getDisplayName(a, 'areas')}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Empleados huérfanos */}
              {sidebarItems.orphanEmp.length > 0 && (
                <div className="organigrama-category">
                  <h3 className="organigrama-category-title">👤 Empleados sin asignar ({sidebarItems.orphanEmp.length})</h3>
                  {sidebarItems.orphanEmp.map(e => {
                    const id = e.idEmpleado || e.IdEmpleado;
                    return (
                      <div key={id} className="organigrama-item"
                        style={{ borderLeft: `3px solid ${LEVELS.empleados.color}` }}
                        draggable
                        onDragStart={(evt) => handleDragStartSidebar(evt, e, 'empleados')}
                      >
                        <span className="organigrama-item-icon">👤</span>
                        <span>{getDisplayName(e, 'empleados')}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Unidades huérfanas */}
              {sidebarItems.orphanUO.length > 0 && (
                <div className="organigrama-category">
                  <h3 className="organigrama-category-title">📂 UOs sin asignar ({sidebarItems.orphanUO.length})</h3>
                  {sidebarItems.orphanUO.map(u => {
                    const id = u.idUnidadOrganizacional || u.IdUnidadOrganizacional;
                    return (
                      <div key={id} className="organigrama-item"
                        style={{ borderLeft: `3px solid ${LEVELS.unidades.color}` }}
                        draggable
                        onDragStart={(e) => handleDragStartSidebar(e, u, 'unidades')}
                      >
                        <span className="organigrama-item-icon">📂</span>
                        <span>{getDisplayName(u, 'unidades')}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Leyenda de jerarquía */}
              <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase' }}>Jerarquía del canvas</p>
                {Object.entries(LEVELS).map(([key, cfg]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', fontSize: '0.78rem' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                    <span style={{ color: '#64748b' }}>{cfg.icon} {cfg.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Toggle button para colapsar sidebar */}
        <button
          type="button"
          className={`organigrama-sidebar-toggle${sidebarOpen ? '' : ' organigrama-sidebar-toggle--collapsed'}`}
          onClick={() => {
            if (typeof window !== 'undefined' && window.innerWidth < 1024) {
              setSidebarOpen(prev => !prev);
            }
          }}
          aria-label={sidebarOpen ? 'Ocultar sidebar' : 'Mostrar sidebar'}
        >
          <span className="organigrama-sidebar-toggle-icon">
            {sidebarOpen ? '‹' : '›'}
          </span>
        </button>
        </div>

        {/* Canvas */}
        <div
          className={`organigrama-canvas${connectMode ? ' connect-mode' : ''}`}
          onDrop={handleDropCanvas}
          onDragOver={handleDragOverCanvas}
          ref={canvasRef}
        >
          <div className="organigrama-canvas-inner">

            {/* Etiquetas de columna eliminadas por solicitud del usuario */}

            {/* SVG de conexiones */}
            <svg className="organigrama-svg-layer">
              <defs>
                <marker id="arrow-db" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#059669" />
                </marker>
                <marker id="arrow-manual" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#7c3aed" />
                </marker>
              </defs>

              {connections.map(conn => {
                const fromNode = nodes.find(n => n.id === conn.from);
                const toNode = nodes.find(n => n.id === conn.to);
                if (!fromNode || !toNode) return null;

                const from = getNodeCenter(fromNode);
                const to = getNodeCenter(toNode);
                const mx = (from.x + to.x) / 2;
                const my = (from.y + to.y) / 2;
                const dx = to.x - from.x;
                const d = `M ${from.x} ${from.y} C ${from.x + dx * 0.5} ${from.y}, ${to.x - dx * 0.5} ${to.y}, ${to.x} ${to.y}`;

                const isDB = conn.fromDB;
                const stroke = isDB ? '#059669' : '#7c3aed';
                const marker = isDB ? 'url(#arrow-db)' : 'url(#arrow-manual)';
                const dash = isDB ? 'none' : '6 3';
                const opacity = isDB ? 0.7 : 1;

                return (
                  <g key={conn.id} className="organigrama-connection-group">
                    <path
                      d={d}
                      stroke={stroke}
                      strokeWidth={isDB ? 2 : 2}
                      strokeDasharray={dash}
                      fill="none"
                      opacity={opacity}
                      markerEnd={marker}
                      style={isDB ? {} : { animation: 'dash-flow 1s linear infinite' }}
                    />
                    {/* Etiqueta de la conexión */}
                    {conn.label && (
                      <text
                        x={mx} y={my - 6}
                        textAnchor="middle"
                        fontSize="9"
                        fill={stroke}
                        opacity={0.7}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {conn.label}
                      </text>
                    )}
                    {/* Botón de eliminar */}
                    <g
                      className="organigrama-connection-delete"
                      transform={`translate(${mx}, ${my})`}
                      onClick={() => handleDeleteConnection(conn.id)}
                    >
                      <circle r="9" />
                      <text textAnchor="middle" dominantBaseline="central" fontSize="11">✕</text>
                    </g>
                  </g>
                );
              })}
            </svg>

            {/* Nodos */}
            {nodes.map(node => {
              const cfg = getLevelConfig(node.type);
              const isEmpleado = node.type === 'empleados';
              const puestoNombre = isEmpleado ? (node.itemData?._puestoNombre || '') : '';
              const fotoUrl = isEmpleado ? (node.itemData?._fotoUrl || null) : null;
              const displayName = getDisplayName(node.itemData, node.type);
              const initials = displayName
                ? displayName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
                : '?';
              return (
                <div
                  id={`org-node-${node.id}`}
                  key={node.id}
                  className={`organigrama-node${connectingFrom === node.id ? ' connecting-source' : ''}${node.fromDB ? ' node-from-db' : ''}${isEmpleado ? ' organigrama-node-empleado' : ''}`}
                  style={{
                    left: node.position.x,
                    top: node.position.y,
                    borderTop: `4px solid ${cfg.color}`,
                  }}
                  onPointerDown={(e) => handleNodePointerDown(e, node.id)}
                  onClick={(e) => handleNodeClickConnect(e, node.id)}
                >
                  <div className="organigrama-node-header">
                    <span style={{ color: cfg.color }}>
                      {cfg.icon} {cfg.label}
                    </span>
                    <button
                      className="remove-node-btn"
                      onClick={(e) => { e.stopPropagation(); handleRemoveNode(node.id); }}
                      title="Remover del canvas"
                    >×</button>
                  </div>

                  {/* Cuerpo de la tarjeta empleado: nombre → foto → puesto (vertical) */}
                  {isEmpleado ? (
                    <div className="organigrama-empleado-body">
                      <div className="organigrama-node-title organigrama-empleado-nombre">
                        {displayName}
                      </div>
                      <div className="organigrama-empleado-foto">
                        {fotoUrl ? (
                          <img
                            src={fotoUrl}
                            alt={displayName}
                            className="organigrama-empleado-foto-img"
                            draggable="false"
                            onDragStart={(e) => e.preventDefault()}
                          />
                        ) : (
                          <div className="organigrama-empleado-foto-initials">
                            {initials}
                          </div>
                        )}
                      </div>
                      <div className="organigrama-node-puesto">
                        <span className="organigrama-node-puesto-icon">💼</span>
                        <span className="organigrama-node-puesto-text">
                          {puestoNombre || 'Sin puesto asignado'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="organigrama-node-title">
                      {displayName}
                    </div>
                  )}

                  {node.fromDB && (
                    <div style={{ fontSize: '0.65rem', color: '#059669', marginTop: '4px', opacity: 0.8 }}>
                      ● BD
                    </div>
                  )}
                </div>
              );
            })}

            {/* Estado vacío */}
            {!loading && nodes.length === 0 && (
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center', color: '#94a3b8', pointerEvents: 'none'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗺️</div>
                <p style={{ fontSize: '1rem', fontWeight: 600 }}>No hay relaciones registradas en BD</p>
                <p style={{ fontSize: '0.85rem' }}>Arrastra elementos desde la sidebar o asigna empleados a áreas en el catálogo</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
